import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Users, CloudCog, Package, Briefcase, ClipboardList, DollarSign, FileDown, Download } from "lucide-react";
import { api, downloadFile, fmtDate, STATUS_STYLES } from "@/lib/api";

const StatCard = ({ icon: Icon, label, value, testid, prefix = "" }) => (
  <div className="rounded-lg border border-navy/10 bg-white p-6" data-testid={testid}>
    <Icon className="h-5 w-5 text-golddark" />
    <p className="mt-3 font-serif text-3xl font-semibold text-navy" data-testid={`${testid}-value`}>{prefix}{value}</p>
    <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-navy/50">{label}</p>
  </div>
);

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/api/admin/stats").then((r) => setStats(r.data));
  }, []);
  if (!stats) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow">TSM Administrator Portal</p>
          <h1 className="mt-3 font-serif text-4xl font-semibold text-navy">Platform overview</h1>
        </div>
        <button
          type="button"
          onClick={() => downloadFile("/api/admin/leads/export", "TSM_leads.csv")}
          data-testid="admin-export-leads"
          className="inline-flex items-center gap-2 rounded-sm border border-navy/15 bg-white px-4 py-2 text-sm font-semibold text-navy transition-colors hover:border-gold"
        >
          <Download className="h-4 w-4" />
          Download leads CSV
        </button>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-stats">
        <StatCard icon={Users} label="Customer accounts" value={stats.total_customers} testid="stat-customers" />
        <StatCard icon={CloudCog} label="Active Workspace subscribers" value={stats.active_subscribers} testid="stat-subscribers" />
        <StatCard icon={DollarSign} label="Monthly recurring revenue" value={`$${stats.mrr.toFixed(0)}`} testid="stat-mrr" />
        <StatCard icon={Package} label="Toolkit purchases" value={stats.toolkit_purchases} testid="stat-toolkits" />
        <StatCard icon={Briefcase} label="Open service requests" value={stats.open_service_requests} testid="stat-open-requests" />
        <StatCard icon={ClipboardList} label="Assessment leads" value={stats.assessment_leads} testid="stat-leads" />
        <StatCard icon={FileDown} label="Sample downloads" value={stats.sample_downloads} testid="stat-sample-downloads" />
        <StatCard icon={Users} label="Interest list signups" value={stats.interest_signups ?? 0} testid="stat-interest" />
        <StatCard icon={ClipboardList} label="Document checkup requests" value={stats.checkup_requests ?? 0} testid="stat-checkups" />
        <StatCard icon={FileDown} label="Workshop registrations" value={stats.workshop_registrations ?? 0} testid="stat-workshop-registrations" />
      </div>
      <p className="text-xs italic text-ink/50">
        Revenue figures reflect platform billing records. {stats.past_due_subscribers > 0 ? `${stats.past_due_subscribers} subscription(s) are past due.` : "No past-due subscriptions."}
      </p>
    </div>
  );
};

export const AdminCustomers = () => {
  const [customers, setCustomers] = useState(null);
  useEffect(() => {
    api.get("/api/admin/customers").then((r) => setCustomers(r.data));
  }, []);
  if (!customers) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-customers">
      <h1 className="font-serif text-4xl font-semibold text-navy">Customers</h1>
      <div className="overflow-x-auto rounded-lg border border-navy/10 bg-white">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead>
            <tr className="border-b border-navy/10 bg-paper font-mono text-[10px] uppercase tracking-[0.14em] text-navy/50">
              {["Business", "Contact", "Industry", "Plan", "Toolkit", "Onboarded", "Records (R/S/D)", "Joined"].map((h) => (
                <th key={h} className="px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-ink/50">No customers yet.</td></tr>}
            {customers.map((c) => (
              <tr key={c.id} className="border-b border-navy/5" data-testid={`admin-customer-${c.id}`}>
                <td className="px-4 py-3 font-semibold text-navy">{c.business_name}</td>
                <td className="px-4 py-3 text-ink/60">{c.email}</td>
                <td className="max-w-[200px] truncate px-4 py-3 text-ink/60">{c.industry || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${c.subscription_status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-navy/5 text-navy/60 border-navy/10"}`}>
                    {c.subscription_status}
                  </span>
                </td>
                <td className="px-4 py-3 text-ink/60">{c.toolkit_owned ? "Owned" : "—"}</td>
                <td className="px-4 py-3 text-ink/60">{c.onboarding_completed ? "Yes" : "Pending"}</td>
                <td className="px-4 py-3 text-ink/60">{c.usage.risks} / {c.usage.sops} / {c.usage.documents}</td>
                <td className="px-4 py-3 text-ink/60">{fmtDate(c.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs italic text-ink/50">
        Tenant isolation: customer business data is visible to the owning customer only. This view shows account metadata and usage counts, not customer records.
      </p>
    </div>
  );
};

export const AdminRequests = () => {
  const [requests, setRequests] = useState(null);
  const load = () => api.get("/api/admin/requests").then((r) => setRequests(r.data));
  useEffect(() => { load(); }, []);
  if (!requests) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  const setStatus = async (rid, status) => {
    await api.put(`/api/admin/requests/${rid}`, { status, note: "" });
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-requests">
      <h1 className="font-serif text-4xl font-semibold text-navy">Service requests</h1>
      <div className="overflow-x-auto rounded-lg border border-navy/10 bg-white">
        <table className="w-full min-w-[980px] text-left text-sm">
          <thead>
            <tr className="border-b border-navy/10 bg-paper font-mono text-[10px] uppercase tracking-[0.14em] text-navy/50">
              {["ID", "Customer", "Service", "Details", "Preferred date", "Status", "Set status"].map((h) => (
                <th key={h} className="px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-ink/50">No service requests yet.</td></tr>}
            {requests.map((r) => (
              <tr key={r.request_id + (r.customer_email || "")} className="border-b border-navy/5 align-top" data-testid={`admin-request-${r.request_id}`}>
                <td className="px-4 py-3 font-mono text-xs font-semibold text-navy">{r.request_id}</td>
                <td className="px-4 py-3">
                  <p className="font-medium text-navy">{r.customer_business || "—"}</p>
                  <p className="text-xs text-ink/50">{r.customer_email}</p>
                </td>
                <td className="px-4 py-3 text-ink/70">{r.service_name}</td>
                <td className="max-w-xs px-4 py-3 text-ink/60">{(r.details || "").slice(0, 120)}</td>
                <td className="px-4 py-3 text-ink/60">{fmtDate(r.preferred_date)}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${STATUS_STYLES[r.status] || "bg-navy/5 text-navy/60 border-navy/10"}`}>{r.status}</span>
                </td>
                <td className="px-4 py-3">
                  <select
                    value={["Submitted", "In Review", "Scheduled", "Completed", "Declined"].includes(r.status) ? r.status : "Submitted"}
                    onChange={(e) => setStatus(r.request_id, e.target.value)}
                    data-testid={`admin-request-status-${r.request_id}`}
                    className="rounded-sm border border-navy/15 bg-white px-3 py-2 text-xs font-semibold text-navy"
                  >
                    {["Submitted", "In Review", "Scheduled", "Completed", "Declined"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const AdminConfig = () => {
  const [config, setConfig] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/api/admin/config").then((r) => setConfig(r.data));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setSaved(false);
    setError("");
    try {
      await api.put("/api/admin/config", {
        toolkit_price: Number(config.toolkit_price),
        pro_monthly_price: Number(config.pro_monthly_price),
        toolkit_access_days: Number(config.toolkit_access_days),
        grace_days: Number(config.grace_days),
        toolkit_sales_enabled: !!config.toolkit_sales_enabled,
        drug_testing_available: !!config.drug_testing_available,
        fingerprinting_available: !!config.fingerprinting_available,
      });
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Saving failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!config) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;
  const f = "w-full rounded-none border-0 border-b-2 border-navy/15 bg-transparent px-0 py-2 text-navy focus:border-gold focus:outline-none";
  const l = "mb-1 block font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/60";

  return (
    <div className="mx-auto max-w-2xl" data-testid="admin-config">
      <h1 className="font-serif text-4xl font-semibold text-navy">Pricing & config</h1>
      <p className="mt-2 text-sm text-ink/60">
        Prices shown on the sales page and charged at checkout come from here — nothing is hard-coded. Saving also updates the
        prices in your Stripe account automatically.
      </p>
      <form onSubmit={save} className="mt-8 rounded-lg border border-navy/10 bg-white p-8" data-testid="admin-config-form">
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <label className={l} htmlFor="cfg-toolkit">Toolkit price (USD, one-time)</label>
            <input id="cfg-toolkit" type="number" step="0.01" min="0" required className={f} value={config.toolkit_price} onChange={(e) => setConfig({ ...config, toolkit_price: e.target.value })} data-testid="config-toolkit-price" />
          </div>
          <div>
            <label className={l} htmlFor="cfg-pro">Pro price (USD / month)</label>
            <input id="cfg-pro" type="number" step="0.01" min="0" required className={f} value={config.pro_monthly_price} onChange={(e) => setConfig({ ...config, pro_monthly_price: e.target.value })} data-testid="config-pro-price" />
          </div>
          <div>
            <label className={l} htmlFor="cfg-days">Toolkit download window (days)</label>
            <input id="cfg-days" type="number" min="1" required className={f} value={config.toolkit_access_days} onChange={(e) => setConfig({ ...config, toolkit_access_days: e.target.value })} data-testid="config-access-days" />
          </div>
          <div>
            <label className={l} htmlFor="cfg-grace">Read-only grace period after cancellation (days)</label>
            <input id="cfg-grace" type="number" min="0" required className={f} value={config.grace_days} onChange={(e) => setConfig({ ...config, grace_days: e.target.value })} data-testid="config-grace-days" />
          </div>
        </div>
        <label className="mt-6 flex items-start gap-3 rounded-sm border border-navy/10 bg-paper p-4" data-testid="config-sales-toggle-wrap">
          <input
            type="checkbox"
            checked={!!config.toolkit_sales_enabled}
            onChange={(e) => setConfig({ ...config, toolkit_sales_enabled: e.target.checked })}
            data-testid="config-sales-toggle"
            className="mt-0.5 h-4 w-4 shrink-0 accent-[#C5A059]"
          />
          <span className="text-sm text-navy/80">
            <strong>Storefront open</strong> — when unchecked, checkout is paused and visitors are told the storefront is
            temporarily unavailable (nothing can be purchased).
          </span>
        </label>
        {saved && <p className="mt-5 rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="config-saved">Saved — Stripe prices updated.</p>}
        {error && <p className="mt-5 rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="config-error">{error}</p>}
        <div className="mt-8 border-t border-navy/10 pt-8">
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/60">TSM4 service availability</p>
          <p className="mt-2 text-xs leading-relaxed text-ink/55">
            Planned services are shown as &ldquo;Coming Soon&rdquo; with an interest list. When a service officially launches,
            switch it on here — its page switches to a live service request form the moment you save. Leave off until the
            service (and any payment collection) is truly ready.
          </p>
          <label className="mt-4 flex items-start gap-3 rounded-sm border border-navy/10 bg-paper p-4" data-testid="config-drug-testing-toggle-wrap">
            <input
              type="checkbox"
              checked={!!config.drug_testing_available}
              onChange={(e) => setConfig({ ...config, drug_testing_available: e.target.checked })}
              data-testid="config-drug-testing-toggle"
              className="mt-0.5 h-4 w-4 shrink-0 accent-[#147D80]"
            />
            <span className="text-sm text-navy/80"><strong>TSM Drug Testing is operational</strong> — show a live service request form instead of the interest list.</span>
          </label>
          <label className="mt-3 flex items-start gap-3 rounded-sm border border-navy/10 bg-paper p-4" data-testid="config-fingerprinting-toggle-wrap">
            <input
              type="checkbox"
              checked={!!config.fingerprinting_available}
              onChange={(e) => setConfig({ ...config, fingerprinting_available: e.target.checked })}
              data-testid="config-fingerprinting-toggle"
              className="mt-0.5 h-4 w-4 shrink-0 accent-[#147D80]"
            />
            <span className="text-sm text-navy/80"><strong>TSM Fingerprinting is operational</strong> — activate bookings (shows a live service request form instead of the waitlist).</span>
          </label>
        </div>
        <button type="submit" disabled={busy} data-testid="config-save" className="btn-gold mt-8 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Save & Sync Stripe
        </button>
      </form>
    </div>
  );
};

export const AdminOrders = () => {
  const [orders, setOrders] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/api/admin/orders").then((r) => setOrders(r.data)).catch(() => setError("Orders could not be loaded."));
  }, []);

  const act = async (orderId, action, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusyId(orderId + action);
    setNotice("");
    setError("");
    try {
      const r = await api.post(`/api/admin/orders/${orderId}/${action}`);
      setNotice(r.data.message || "Done.");
      setOrders((os) => os.map((o) => (o.id === orderId ? { ...o, status: action === "refund" ? "refunded" : o.status, refunded: action === "refund" ? true : o.refunded } : o)));
    } catch (err) {
      setError(err.response?.data?.detail || "Action failed.");
    } finally {
      setBusyId("");
    }
  };

  if (!orders) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  return (
    <div className="mx-auto max-w-5xl" data-testid="admin-orders">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl font-semibold text-navy">Toolkit orders</h1>
          <p className="mt-2 text-sm text-ink/60">Every checkout — guests and signed-in customers. Resend secure download links or refund through Stripe.</p>
        </div>
        <Link to="/admin" className="text-sm font-semibold text-golddark hover:text-navy" data-testid="orders-back-link">← Back to overview</Link>
      </div>
      {notice && <p className="mt-6 rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="orders-notice">{notice}</p>}
      {error && <p className="mt-6 rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="orders-error">{error}</p>}
      <div className="mt-8 overflow-x-auto rounded-lg border border-navy/10 bg-white">
        <table className="min-w-full text-left text-sm" data-testid="orders-table">
          <thead>
            <tr className="border-b border-navy/10 bg-paper font-mono text-[10px] uppercase tracking-wider text-navy/60">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Downloads</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-b border-navy/5 last:border-0" data-testid="order-row">
                <td className="px-4 py-3 text-ink/70">{fmtDate(o.created_at)}</td>
                <td className="px-4 py-3 font-medium text-navy">
                  {o.product}
                  {o.is_guest && <span className="ml-2 rounded-full bg-paper px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-ink/50">guest</span>}
                </td>
                <td className="px-4 py-3 text-ink/70">{o.customer}{o.business ? ` · ${o.business}` : ""}</td>
                <td className="px-4 py-3 font-semibold text-navy">${o.amount?.toFixed(2)} {o.currency}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${o.refunded ? "border-red-200 bg-red-50 text-red-700" : o.status === "paid" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`} data-testid="order-status">
                    {o.refunded ? "refunded" : o.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-ink/70">
                  {o.product.includes("Toolkit") ? `${o.downloads || 0}${o.download_expires ? ` · expires ${fmtDate(o.download_expires)}` : ""}` : "—"}
                </td>
                <td className="px-4 py-3">
                  {o.product.includes("Toolkit") && o.status === "paid" && !o.refunded ? (
                    <div className="flex gap-2">
                      <button type="button" onClick={() => act(o.id, "resend-link")} disabled={busyId === o.id + "resend-link"} data-testid="order-resend" className="rounded-sm border border-navy/15 px-2.5 py-1.5 text-xs font-semibold text-navy transition-colors hover:border-gold disabled:opacity-50">
                        Resend link
                      </button>
                      <button type="button" onClick={() => act(o.id, "refund", `Refund $${o.amount?.toFixed(2)} to ${o.customer} via Stripe? This cannot be undone.`)} disabled={busyId === o.id + "refund"} data-testid="order-refund" className="rounded-sm border border-red-200 px-2.5 py-1.5 text-xs font-semibold text-red-700 transition-colors hover:bg-red-50 disabled:opacity-50">
                        Refund
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-ink/40">—</span>
                  )}
                </td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-10 text-center text-ink/50">No orders yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-xs text-ink/45">Refunds are issued through the connected Stripe account and revoke download access immediately.</p>
    </div>
  );
};

